import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Facebook, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    label: "Phone",
    value: "651-703-4734",
    href: "tel:651-703-4734",
  },
  {
    icon: Mail,
    label: "Email",
    value: "confidencetocreate@gmail.com",
    href: "mailto:confidencetocreate@gmail.com",
  },
  {
    icon: MapPin,
    label: "Address",
    value: "670 W Wheelock Parkway, Suite 6, Saint Paul, MN 55117",
    href: "https://maps.google.com/?q=670+W+Wheelock+Parkway+Suite+6+Saint+Paul+MN+55117",
  },
  {
    icon: Facebook,
    label: "Facebook",
    value: "@Confidence2CreateInc",
    href: "https://www.facebook.com/Confidence2CreateInc",
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      <SEO
        title="Contact Us"
        description="Get in touch with Confidence 2 Create Inc. at 670 Wheelock Parkway, Suite 6, St. Paul, MN 55117. Call 651-703-4734 or email us."
      />
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/f909bg7t_Image%201.jpg" 
            alt="Community" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block bg-[#FFC107] text-[#0033A0] px-4 py-2 rounded-full text-sm font-bold mb-6">
              Get In Touch
            </span>
            <h1 className="text-3xl md:text-6xl font-black text-white mb-6">
              Contact <span className="text-[#FFC107]">Us</span>
            </h1>
            <p className="text-base md:text-xl text-white/90 max-w-2xl mx-auto">
              We'd love to hear from you! Whether you want to volunteer, donate, or learn more about our programs, reach out today.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-[#0033A0] mb-8">Send Us a Message</h2>
              
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid="contact-success-message">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-green-700 mb-2">Thank You!</h3>
                  <p className="text-green-600">Your message has been sent. We'll get back to you soon!</p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="mt-6 text-[#0033A0] font-semibold hover:underline"
                    data-testid="send-another-btn"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      data-testid="contact-input-name"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20 transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      data-testid="contact-input-email"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20 transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      data-testid="contact-input-phone"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20 transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      data-testid="contact-input-message"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20 transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-[#0033A0] mb-8">Contact Information</h2>
              
              <div className="space-y-6 mb-12">
                {contactInfo.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    target={item.icon === Facebook || item.icon === MapPin ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`contact-info-${item.label.toLowerCase()}`}
                    className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-12 h-12 bg-[#0033A0] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#FFC107] transition-colors">
                      <item.icon className="w-5 h-5 text-white group-hover:text-[#0033A0] transition-colors" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{item.label}</p>
                      <p className="text-slate-600 group-hover:text-[#0033A0] transition-colors">
                        {item.value}
                      </p>
                    </div>
                  </a>
                ))}
              </div>

              {/* Office Hours */}
              <div className="bg-slate-50 p-8 rounded-2xl" data-testid="office-hours">
                <h3 className="text-xl font-bold text-[#0033A0] mb-4">Office Hours</h3>
                <div className="space-y-2 text-slate-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span className="font-semibold">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span className="font-semibold">By Appointment Only</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-semibold">Closed</span>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="mt-8 rounded-2xl overflow-hidden shadow-lg" data-testid="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2822.0!2d-93.1057!3d44.9745!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87f7d5a44b5a5555%3A0x0!2s670+Wheelock+Pkwy%2C+St+Paul%2C+MN+55117!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="C2C Location"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
